import { useEffect, useId, useRef } from 'react';
import { BarChart3, Radio, Trophy } from 'lucide-react';
import DrawerHeader from '../studio/DrawerHeader';
import { FOCUSABLE_SELECTOR, focusableWithin, wrapTabFocus } from '../../lib/focusTrap';
import { formatTimestamp } from '../../lib/publisherFormat';
import { c } from '../../styles/theme';
import type { PublisherDashboardActivityItem } from '../../lib/publisherDashboard';
import type { PublisherReleasePlatform } from '../../types/domain';
import PublisherOutletGlyphs from './PublisherOutletGlyphs';

export interface PublisherActivityDrawerProps {
  item: PublisherDashboardActivityItem;
  platforms: readonly PublisherReleasePlatform[];
  onClose: () => void;
  onOpenRelease?: (releaseId: string) => void;
}

function drawerTitle(item: PublisherDashboardActivityItem): string {
  if (item.type === 'earning') return 'Gross Reported';
  return item.event.message;
}

function drawerSummary(item: PublisherDashboardActivityItem): string | null {
  const title = drawerTitle(item);
  if (item.event.message !== title) return item.event.message;
  return null;
}

function drawerIcon(item: PublisherDashboardActivityItem) {
  if (item.type === 'earning') return <BarChart3 size={15} color={c.accent} />;
  if (item.type === 'milestone' && item.event.message === 'Milestone reached') {
    return <Trophy size={15} color={c.accent} />;
  }
  return <Radio size={15} color={c.accent} />;
}

function dedupePlatforms(platforms: readonly PublisherReleasePlatform[]): PublisherReleasePlatform[] {
  const seen = new Set<string>();
  return [...platforms]
    .filter((platform) => {
      if (seen.has(platform.outletKey)) return false;
      seen.add(platform.outletKey);
      return true;
    })
    .sort((a, b) => a.outletName.localeCompare(b.outletName));
}

export default function PublisherActivityDrawer({
  item,
  platforms,
  onClose,
  onOpenRelease,
}: PublisherActivityDrawerProps) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const headingId = useId();
  const release = item.release;
  const title = drawerTitle(item);
  const summary = drawerSummary(item);
  const relatedPlatforms = dedupePlatforms(platforms);

  useEffect(() => {
    previouslyFocused.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const root = rootRef.current;
    if (root) {
      const firstFocusable =
        focusableWithin(root)[0] ?? root.querySelector<HTMLElement>(FOCUSABLE_SELECTOR) ?? root;
      firstFocusable.focus();
    }
    return () => {
      const target = previouslyFocused.current;
      previouslyFocused.current = null;
      if (target?.isConnected) target.focus();
    };
  }, []);

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const root = rootRef.current;
      if (!root) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      wrapTabFocus(root, event);
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [onClose]);

  return (
    <>
      <div className="lyx-drawer-overlay" onClick={onClose} aria-hidden="true" />
      <div
        ref={rootRef}
        className="lyx-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        tabIndex={-1}
        style={{
          borderLeft: `1px solid ${c.border}`,
          background: c.bg,
          padding: 16,
          boxShadow: '-14px 0 42px rgba(0,0,0,0.28)',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}
      >
        <div id={headingId} style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden' }}>
          {title} activity details
        </div>
        <DrawerHeader title={title} backLabel={title} onBack={onClose} onClose={onClose} />

        <div
          style={{
            border: `1px solid ${c.border}`,
            borderRadius: 12,
            background: c.surface1,
            padding: '14px 16px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <div
              aria-hidden="true"
              style={{
                width: 32,
                height: 32,
                borderRadius: 9,
                background: c.surface2,
                border: `1px solid ${c.border}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {drawerIcon(item)}
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: c.text1 }}>{title}</p>
                {(item.event.message === 'Release Approved' || item.event.message === 'Release Distributed') && relatedPlatforms.length > 0 ? (
                  <PublisherOutletGlyphs platforms={relatedPlatforms} ariaLabel={`Reported outlets for ${title}: ${relatedPlatforms.map((platform) => platform.outletName).join(', ')}`} />
                ) : null}
              </div>
              <p style={{ margin: '0 0 4px', fontSize: 11.5, color: c.text2, lineHeight: 1.55 }}>
                {release?.title.trim() || 'Untitled release'}
                {release ? ` · ${release.releaseType}` : ''}
                {release?.artistName.trim() ? ` · ${release.artistName.trim()}` : ''}
              </p>
              {summary ? (
                <p style={{ margin: '0 0 4px', fontSize: 11.5, color: c.text2, lineHeight: 1.55 }}>{summary}</p>
              ) : null}
              <p style={{ margin: 0, fontSize: 11, color: c.text2 }}>
                {formatTimestamp(item.event.at)}
                {item.event.source === 'provider' ? ' · reported by the distributor' : ''}
              </p>
            </div>
          </div>
        </div>

        {item.event.detail ? (
          <section
            style={{
              border: `1px solid ${c.border}`,
              borderRadius: 12,
              background: c.surface1,
              padding: '14px 16px',
            }}
          >
            <p
              style={{
                margin: '0 0 8px',
                fontSize: 11,
                color: c.text2,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                fontWeight: 600,
              }}
            >
              Detail
            </p>
            <p style={{ margin: 0, fontSize: 12, color: c.text1, lineHeight: 1.6 }}>{item.event.detail}</p>
          </section>
        ) : null}

        {relatedPlatforms.length > 0 ? (
          <section
            style={{
              border: `1px solid ${c.border}`,
              borderRadius: 12,
              background: c.surface1,
              padding: '14px 16px',
            }}
          >
            <p
              style={{
                margin: '0 0 10px',
                fontSize: 11,
                color: c.text2,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                fontWeight: 600,
              }}
            >
              Reported outlets
            </p>
            <div style={{ display: 'grid', gap: 8 }}>
              {relatedPlatforms.map((platform) => (
                <div
                  key={platform.outletKey}
                  style={{
                    border: `1px solid ${c.border}`,
                    borderRadius: 10,
                    background: c.surface2,
                    padding: '10px 12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 10,
                    flexWrap: 'wrap',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <PublisherOutletGlyphs platforms={[platform]} ariaLabel={platform.outletName} />
                    <p style={{ margin: 0, fontSize: 12, color: c.text1 }}>{platform.outletName}</p>
                  </div>
                  <p style={{ margin: 0, fontSize: 11, color: c.text2 }}>
                    {platform.status || 'No delivery status reported'}
                    {platform.updatedAt ? ` · ${formatTimestamp(platform.updatedAt)}` : ''}
                  </p>
                </div>
              ))}
            </div>
          </section>
        ) : null}

        {release && onOpenRelease ? (
          <button
            type="button"
            onClick={() => onOpenRelease(release.id)}
            className="lyx-publisher-activity-link"
            style={{ alignSelf: 'flex-start' }}
          >
            Open release
          </button>
        ) : null}
      </div>
    </>
  );
}
